import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import toast from 'react-hot-toast';
import { incidentService, Continent, IncidentCount, THREAT_COLORS } from '../../services/incidents';
import { geography } from '../../services/supabase';
import { Country, State, LocalGovernmentArea } from '../../types/geography';
import canadaProvincesData from '../../data/canada_provinces.json';

// @ts-ignore
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

interface HomePageMapProps {
  className?: string;
}

const HomePageMap: React.FC<HomePageMapProps> = ({ className = '' }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Map data
  const [continents, setContinents] = useState<Continent[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [lgas, setLgas] = useState<LocalGovernmentArea[]>([]);
  const [incidentCounts, setIncidentCounts] = useState<IncidentCount[]>([]);
  
  // Map level state
  const [mapLevel, setMapLevel] = useState<'country' | 'state' | 'lga'>('country');
  const [mapLoaded, setMapLoaded] = useState(false);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load continents
        const { data: continentsData, error: continentsError } = await incidentService.getContinents();
        if (continentsError) throw continentsError;
        setContinents(continentsData || []);

        // Load countries
        const { data: countriesData, error: countriesError } = await geography.getCountries();
        if (countriesError) throw countriesError;

        // Load all countries to make them all clickable
        setCountries(countriesData || []);

        // Find and pre-select Canada to show provinces
        const canada = countriesData?.find((c: any) => c.code === 'CAN');
        if (canada) {
          // Load states for Canada
          const { data: statesData, error: statesError } = await geography.getStates(canada.id);
          if (!statesError && statesData) {
            let loadedStates = statesData || [];
            
            // Merge Canadian province boundaries from JSON
            const geoFeatures = (canadaProvincesData as any).features;
            const normalize = (s: string) => s.toLowerCase().replace(/territory|province/g, '').trim();
            
            // Map existing DB states to geometries
            loadedStates = loadedStates.map((state: State) => {
              const feature = geoFeatures.find((f: any) => 
                normalize(f.properties.name) === normalize(state.name)
              );
              if (feature) {
                return { ...state, geometry: feature.geometry };
              }
              return state;
            });
            
            // Add missing provinces from GeoJSON as virtual states
            const processedDbStates = new Set(loadedStates.map((s: State) => normalize(s.name)));
            geoFeatures.forEach((feature: any) => {
              const name = feature.properties.name;
              if (!processedDbStates.has(normalize(name))) {
                loadedStates.push({
                  id: `virtual-${name.replace(/\s+/g, '-')}`,
                  country_id: canada.id,
                  name: name,
                  geometry: feature.geometry,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                } as State);
              }
            });
            
            setStates(loadedStates);
            setMapLevel('state');
            
            // Load incident counts for Canadian provinces
            const stateCountsResult = await incidentService.getIncidentCounts('state', canada.id);
            if (stateCountsResult.data) {
              setIncidentCounts(stateCountsResult.data);
            }
          }
        } else {
          // Fallback to country-level counts if Canada not found
          const countsResult = await incidentService.getIncidentCounts('country');
          if (countsResult.data) {
            setIncidentCounts(countsResult.data);
          }
        }

      } catch (err) {
        console.error('Failed to load map data:', err);
        setError('Failed to load map data');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || loading) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-95, 60], // Center on Canada
      zoom: 3.5, // Zoom level to show Canada prominently
      minZoom: 1,
      maxZoom: 15,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    map.current.on('load', () => {
      setLoading(false);
      setMapLoaded(true);
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [loading]);

  // Update map layers when data changes
  useEffect(() => {
    if (!map.current || loading || !mapLoaded) return;

    const updateMapLayers = () => {
      // Map is loaded and style is ready, proceed with layer updates
      // Clear existing layers
      const layersToRemove = ['areas-labels', 'areas-border', 'areas-fill'];
      const sourcesToRemove = ['areas'];

      layersToRemove.forEach(layerId => {
        if (map.current!.getLayer(layerId)) {
          map.current!.removeLayer(layerId);
        }
      });

      sourcesToRemove.forEach(sourceId => {
        if (map.current!.getSource(sourceId)) {
          map.current!.removeSource(sourceId);
        }
      });

      // Build features based on level
      let features: GeoJSON.Feature[] = [];
      const incidentHeatmap = new Map(incidentCounts.map(item => [item.area_id, item]));

      const createFeature = (item: any, index: number) => {
        const incidentData = incidentHeatmap.get(item.id);
        const threatLevel = incidentData?.threat_level || 1;
        // Use same 12-level color scale as incident-map page for consistency
        const safeLevel = Math.max(1, Math.min(12, Math.round(threatLevel)));
        const color = THREAT_COLORS[safeLevel] || THREAT_COLORS[1];

        return {
          type: 'Feature' as const,
          id: index,
          properties: {
            id: item.id,
            name: item.name,
            incident_count: incidentData?.incident_count || 0,
            threat_level: threatLevel,
            color: color
          },
          geometry: item.geometry as GeoJSON.Geometry
        };
      };

      if (mapLevel === 'country') {
        features = countries
          .filter(item => item.geometry)
          .map(createFeature);
      } else if (mapLevel === 'state') {
        features = states
          .filter(item => item.geometry)
          .map(createFeature);
      } else {
        features = lgas
          .filter(item => item.geometry)
          .map(createFeature);
      }

      if (features.length === 0) return;

      const geojson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features
      };

      // Add source
      map.current!.addSource('areas', {
        type: 'geojson',
        data: geojson
      });

      // Add fill layer
      map.current!.addLayer({
        id: 'areas-fill',
        type: 'fill',
        source: 'areas',
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': 0.7
        }
      });

      // Add border layer
      map.current!.addLayer({
        id: 'areas-border',
        type: 'line',
        source: 'areas',
        paint: {
          'line-color': '#374151',
          'line-width': 1
        }
      });

      // Add labels
      map.current!.addLayer({
        id: 'areas-labels',
        type: 'symbol',
        source: 'areas',
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
          'text-size': 10,
          'text-variable-anchor': ['center'],
          'text-justify': 'center',
        },
        paint: {
          'text-color': '#1f2937',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
        minzoom: 3
      });

      // Add hover effects
      let hoveredFeatureId: number | null = null;

      map.current!.on('mouseenter', 'areas-fill', () => {
        map.current!.getCanvas().style.cursor = 'pointer';
      });

      map.current!.on('mouseleave', 'areas-fill', () => {
        map.current!.getCanvas().style.cursor = '';
        if (hoveredFeatureId !== null) {
          map.current!.setFeatureState(
            { source: 'areas', id: hoveredFeatureId },
            { hover: false }
          );
        }
        hoveredFeatureId = null;
      });

      // Add click handler for navigation
      map.current!.on('click', 'areas-fill', (e) => {
        if (e.features && e.features.length > 0) {
          const props = e.features[0].properties;
          handleMapClick({
            id: props?.id || '',
            name: props?.name || ''
          });
        }
      });
    };

    updateMapLayers();
  }, [loading, mapLoaded, mapLevel, countries, states, lgas, incidentCounts]);

  // Handle map clicks - navigate to incident map page with selection
  const handleMapClick = (feature: { id: string; name: string }) => {
    // Find the selected item based on current map level
    let selectedItem: any = null;
    let selectionType: string = '';

    if (mapLevel === 'country') {
      selectedItem = countries.find(c => c.id === feature.id);
      selectionType = 'country';
    } else if (mapLevel === 'state') {
      selectedItem = states.find(s => s.id === feature.id);
      selectionType = 'state';
    } else if (mapLevel === 'lga') {
      selectedItem = lgas.find(l => l.id === feature.id);
      selectionType = 'lga';
    }

    if (!selectedItem) return;

    // Build full geographical hierarchy for selection
    let selectedCountry: Country | null = null;
    let selectedContinent: Continent | null = null;

    if (selectionType === 'state') {
      // Find the parent country for this state
      selectedCountry = countries.find(c => c.id === selectedItem.country_id) || null;
      if (selectedCountry) {
        // Find the continent for this country
        selectedContinent = continents.find(c => c.id === selectedCountry!.continent_id) || null;
      }
    } else if (selectionType === 'country') {
      selectedCountry = selectedItem;
      selectedContinent = continents.find(c => 
        countries.some(country => country.id === selectedItem.id && country.continent_id === c.id)
      ) || null;
    } else if (selectionType === 'lga') {
      // Find parent state and country for LGA
      const parentState = states.find(s => s.id === selectedItem.state_id);
      if (parentState) {
        selectedCountry = countries.find(c => c.id === parentState.country_id) || null;
        if (selectedCountry) {
          selectedContinent = continents.find(c => c.id === selectedCountry!.continent_id) || null;
        }
      }
    }

    // Navigate to incident map page with full selection hierarchy
    const state = {
      continent: selectedContinent,
      country: selectedCountry,
      state: selectionType === 'state' ? selectedItem : null,
      lga: selectionType === 'lga' ? selectedItem : null
    };

    // Navigate with state in location
    navigate('/incident-map', { 
      state: { 
        selection: state,
        fromHomepage: true 
      } 
    });
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading security intelligence map...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-red-50 ${className}`}>
        <div className="text-center text-red-600">
          <p className="font-semibold">Unable to load map</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div ref={mapContainer} className="w-full h-full rounded-lg" />
      
      {/* Map Legend - matches 12-level scale on incident-map page */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 text-xs">
        <div className="font-semibold text-gray-700 mb-2">Threat Level</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: THREAT_COLORS[12] }}></div>
            <span>Extreme (10-12)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: THREAT_COLORS[8] }}></div>
            <span>High (7-9)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: THREAT_COLORS[5] }}></div>
            <span>Medium (4-6)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: THREAT_COLORS[3] }}></div>
            <span>Low-Med (3)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: THREAT_COLORS[1] }}></div>
            <span>Low (1-2)</span>
          </div>
        </div>
        <div className="mt-3 pt-2 border-t text-gray-500 text-xs">
          Click any region to view detailed intelligence
        </div>
      </div>
    </div>
  );
};

export default HomePageMap;
